import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useUpdateMeMutation } from '@/shared/api/endpoints';
import { selectSessionStatus } from '@/shared/api/sessionSlice';
import { LOCALE_NAMES, LOCALES, rememberLocale, useContentLocale, type Locale } from '@/shared/lib/locale';
import { Select } from '@/shared/ui/primitives';

// The short name of a language is its code, but where the code means little to its readers (中文).
const OPTIONS = LOCALES.map((locale) => ({
  value: locale,
  label: LOCALE_NAMES[locale],
  short: locale === 'zh' ? LOCALE_NAMES[locale] : locale.toUpperCase(),
  lang: locale,
}));

interface LanguageSwitcherProps {
  /** compact: for a menu; regular: for a form. */
  size?: 'sm' | 'md';
  fullWidth?: boolean;
  /** Show the chosen language's code only: for a bar with little room. The list still names each in full. */
  compact?: boolean;
}

/**
 * Switches the interface and content language at once. Every language is listed
 * under its own name, so it can be found whatever language is active.
 */
export function LanguageSwitcher({ size = 'sm', fullWidth, compact }: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation();
  const current = useContentLocale();
  const signedIn = useSelector(selectSessionStatus) === 'authenticated';
  const [updateMe] = useUpdateMeMutation();

  const change = (locale: Locale) => {
    rememberLocale(locale);
    void i18n.changeLanguage(locale);
    // Saved on the account too: a device with no choice of its own starts in it.
    if (signedIn) void updateMe({ locale });
  };

  return (
    <Select
      label={t('language')}
      icon="globe"
      size={size}
      fullWidth={fullWidth}
      compact={compact}
      align="end"
      options={OPTIONS}
      value={current}
      onChange={change}
    />
  );
}
